
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  Alert,
  Container,
  Grid,
  useTheme,
  useMediaQuery,
  Skeleton,
  Button,
  Card,
  CardContent,
  TablePagination,
} from "@mui/material";
import {
  LocalHospital,
  Refresh,
  Medication,
  Inventory,
  ImageNotSupported,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";

const PetCareMedicineList = () => {
  const { id } = useParams(); // Get category ID from URL params

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const baseUrl = "https://api.medstown.com";

  // MedsToWin brand colors
  const medsToWinColors = {
    primary: "#2E7D32",
    secondary: "#4CAF50",
    accent: "#81C784",
    light: "#C8E6C9",
    skeleton: "rgba(46, 125, 50, 0.15)",
    gradient: "linear-gradient(135deg, #2E7D32 0%, #4CAF50 100%)",
  };

  const fetchProducts = async (currentPage = 1, limit = 10) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${baseUrl}/api/medshubs/otc-products?page=${currentPage}&limit=${limit}&cid=${id}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setProducts(data.data.list);
        setTotalCount(data.data.totalCount);
      } else {
        throw new Error(data.message || "Failed to fetch products");
      }
    } catch (err) {
      setError(err.message);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts(page + 1, rowsPerPage);
  }, [page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatPrice = (price) => {
    return `₹${parseInt(price).toLocaleString()}`;
  };

  // COMPACT Header Component - Uses less space and smaller fonts
  const MedicineHeader = () => (
    <Box
      sx={{
        background: medsToWinColors.gradient,
        color: "white",
        py: { xs: 1.5, md: 2 }, // Reduced from 3,4 to 1.5,2
        mb: { xs: 1.5, md: 2 }, // Reduced from 2,3 to 1.5,2
        borderRadius: 2,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: -5,
          right: -5,
          opacity: 0.08,
          fontSize: { xs: "2rem", md: "3rem" }, // Reduced from 3rem,6rem to 2rem,3rem
        }}
      >
        <LocalHospital />
      </Box>
      <Container>
        <Grid container alignItems="center" spacing={{ xs: 1.5, md: 2 }}>
          <Grid item>
            <Box
              sx={{
                backgroundColor: "rgba(255,255,255,0.2)",
                borderRadius: "50%",
                p: { xs: 1, md: 1.2 }, // Reduced from 1.5,2 to 1,1.2
                display: "flex",
              }}
            >
              <Medication sx={{ fontSize: { xs: "1.2rem", md: "1.6rem" } }} />{" "}
              {/* Reduced from 1.8rem,2.5rem */}
            </Box>
          </Grid>
          <Grid item xs>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              fontWeight="600"
              gutterBottom={false}
              sx={{
                fontSize: { xs: "1.2rem", md: "1.8rem" }, // Reduced from 1.75rem,3rem
                mb: 0.5, // Smaller margin bottom
              }}
            >
              Pet Care Medicine List
            </Typography>
            <Typography
              variant="body2"
              sx={{
                opacity: 0.85,
                fontSize: { xs: "0.8rem", md: "0.9rem" }, // Reduced from 1rem,1.25rem
              }}
            >
              Complete catalog of pet healthcare products
            </Typography>
          </Grid>
          <Grid item>
            <Box
              sx={{
                backgroundColor: "rgba(255,255,255,0.15)",
                borderRadius: 1.5,
                p: { xs: 1, md: 1.5 }, // Reduced padding
                minWidth: { xs: 60, md: 80 }, // Reduced width
                textAlign: "center",
              }}
            >
              <Inventory
                sx={{ fontSize: { xs: "1.2rem", md: "1.5rem" }, mb: 0.5 }}
              />{" "}
              {/* Reduced icon size */}
              <Typography
                variant="h6"
                fontWeight="700"
                sx={{ fontSize: { xs: "1rem", md: "1.3rem" } }}
              >
                {totalCount}
              </Typography>
              <Typography
                variant="caption"
                sx={{ opacity: 0.8, fontSize: { xs: "0.7rem", md: "0.75rem" } }}
              >
                Products
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );

  // Mobile Product Card
  const MobileProductCard = ({ product }) => (
    <Card
      sx={{
        mb: 2,
        borderLeft: `5px solid ${medsToWinColors.primary}`,
        boxShadow: 3,
        cursor: "pointer",
        "&:hover": {
          boxShadow: 6,
          transform: "translateY(-2px)",
          transition: "all 0.3s ease",
        },
      }}
      onClick={() => navigate(`/admin/otc-product-individual-page/${product.sku_id}`)}
    >
      <CardContent sx={{ p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={3}>
            {product.image ? (
              <Avatar
                src={product.image}
                alt={product.name}
                sx={{
                  width: { xs: 60, sm: 70 },
                  height: { xs: 60, sm: 70 },
                  borderRadius: 2,
                }}
                variant="rounded"
              />
            ) : (
              <Avatar
                sx={{
                  width: { xs: 60, sm: 70 },
                  height: { xs: 60, sm: 70 },
                  backgroundColor: medsToWinColors.light,
                  color: medsToWinColors.primary,
                  borderRadius: 2,
                }}
                variant="rounded"
              >
                <ImageNotSupported />
              </Avatar>
            )}
          </Grid>
          <Grid item xs={9}>
            <Typography
              variant="h6"
              fontSize={{ xs: "0.95rem", sm: "1.1rem" }}
              fontWeight="600"
              color={medsToWinColors.primary}
              gutterBottom
              sx={{
                lineHeight: 1.3,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {product.name}
            </Typography>

            <Box sx={{ mb: 1 }}>
              <Chip
                label={`ID: ${product.id}`}
                size="small"
                sx={{
                  backgroundColor: medsToWinColors.light,
                  color: medsToWinColors.primary,
                  fontWeight: "bold",
                  fontSize: "0.7rem",
                  mr: 1,
                }}
              />
              <Chip
                label={product.type.toUpperCase()}
                size="small"
                sx={{
                  backgroundColor: "#e3f2fd",
                  color: "#1976d2",
                  fontWeight: "bold",
                  fontSize: "0.7rem",
                }}
              />
            </Box>

            <Typography
              variant="body2"
              fontSize="0.8rem"
              color="textSecondary"
              gutterBottom
              sx={{ mb: 1 }}
            >
              {product.pack_size_label}
            </Typography>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                variant="h6"
                fontSize="1.1rem"
                color={medsToWinColors.primary}
                fontWeight="bold"
              >
                {formatPrice(product.price)}
              </Typography>
              <Typography
                variant="caption"
                fontSize="0.7rem"
                color="textSecondary"
              >
                SKU: {product.sku_id}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  // Desktop Table Skeleton Loading
  const TableSkeletonLoader = () => (
    <Paper sx={{ boxShadow: 4, borderRadius: 3 }}>
      <TableContainer sx={{ borderRadius: 3 }}>
        <Table sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: medsToWinColors.light }}>
              {[
                "Image",
                "Product Details",
                "ID",
                "Pack Size",
                "Price",
                "Type",
                "SKU",
              ].map((header) => (
                <TableCell
                  key={header}
                  sx={{ fontWeight: "bold", color: medsToWinColors.primary }}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from(new Array(rowsPerPage)).map((_, index) => (
              <TableRow key={index}>
                <TableCell>
                  <Skeleton
                    variant="rounded"
                    width={60}
                    height={60}
                    sx={{ bgcolor: medsToWinColors.skeleton }}
                  />
                </TableCell>
                <TableCell>
                  <Skeleton
                    variant="text"
                    width="90%"
                    height={24}
                    sx={{ bgcolor: medsToWinColors.skeleton, mb: 1 }}
                  />
                  <Skeleton
                    variant="text"
                    width="70%"
                    height={16}
                    sx={{ bgcolor: medsToWinColors.skeleton }}
                  />
                </TableCell>
                <TableCell>
                  <Skeleton
                    variant="text"
                    width="80%"
                    height={20}
                    sx={{ bgcolor: medsToWinColors.skeleton }}
                  />
                </TableCell>
                <TableCell>
                  <Skeleton
                    variant="text"
                    width="85%"
                    height={16}
                    sx={{ bgcolor: medsToWinColors.skeleton }}
                  />
                </TableCell>
                <TableCell>
                  <Skeleton
                    variant="text"
                    width="70%"
                    height={24}
                    sx={{ bgcolor: medsToWinColors.skeleton }}
                  />
                </TableCell>
                <TableCell>
                  <Skeleton
                    variant="rounded"
                    width={50}
                    height={24}
                    sx={{ bgcolor: medsToWinColors.skeleton }}
                  />
                </TableCell>
                <TableCell>
                  <Skeleton
                    variant="text"
                    width="80%"
                    height={14}
                    sx={{ bgcolor: medsToWinColors.skeleton }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={() => {}}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={() => {}}
        rowsPerPageOptions={[10, 25, 50, 100]}
        disabled
        sx={{ borderTop: `1px solid ${medsToWinColors.light}` }}
      />
    </Paper>
  );

  // Mobile Skeleton Loading
  const MobileSkeletonLoader = () => (
    <Box>
      {Array.from(new Array(rowsPerPage)).map((_, index) => (
        <Card
          key={index}
          sx={{ mb: 2, borderLeft: `5px solid ${medsToWinColors.primary}` }}
        >
          <CardContent sx={{ p: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={3}>
                <Skeleton
                  variant="rounded"
                  width={60}
                  height={60}
                  sx={{ bgcolor: medsToWinColors.skeleton }}
                />
              </Grid>
              <Grid item xs={9}>
                <Skeleton
                  variant="text"
                  width="95%"
                  height={22}
                  sx={{ bgcolor: medsToWinColors.skeleton, mb: 1 }}
                />
                <Skeleton
                  variant="text"
                  width="75%"
                  height={22}
                  sx={{ bgcolor: medsToWinColors.skeleton, mb: 1 }}
                />
                <Box display="flex" gap={1} mb={1}>
                  <Skeleton
                    variant="rounded"
                    width={60}
                    height={24}
                    sx={{ bgcolor: medsToWinColors.skeleton }}
                  />
                  <Skeleton
                    variant="rounded"
                    width={40}
                    height={24}
                    sx={{ bgcolor: medsToWinColors.skeleton }}
                  />
                </Box>
                <Skeleton
                  variant="text"
                  width="80%"
                  height={16}
                  sx={{ bgcolor: medsToWinColors.skeleton, mb: 1 }}
                />
                <Box display="flex" justifyContent="space-between">
                  <Skeleton
                    variant="text"
                    width={80}
                    height={24}
                    sx={{ bgcolor: medsToWinColors.skeleton }}
                  />
                  <Skeleton
                    variant="text"
                    width={60}
                    height={14}
                    sx={{ bgcolor: medsToWinColors.skeleton }}
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ))}
    </Box>
  );

  // Error State Component
  const ErrorState = () => (
    <Alert
      severity="error"
      sx={{
        mb: 3,
        borderRadius: 3,
        boxShadow: 2,
        "& .MuiAlert-icon": {
          color: medsToWinColors.primary,
        },
      }}
      action={
        <Button
          size="small"
          onClick={() => fetchProducts(page + 1, rowsPerPage)}
          startIcon={<Refresh />}
          sx={{
            color: medsToWinColors.primary,
            "&:hover": {
              backgroundColor: medsToWinColors.light,
            },
          }}
        >
          Retry
        </Button>
      }
    >
      <Typography variant="h6" fontWeight="600" gutterBottom>
        Error Loading Medicine List
      </Typography>
      <Typography variant="body2">{error}</Typography>
    </Alert>
  );

  // No Data State
  const NoDataState = () => (
    <Paper
      sx={{
        p: 6,
        textAlign: "center",
        borderRadius: 3,
        boxShadow: 2,
        backgroundColor: "#fafafa",
      }}
    >
      <Medication sx={{ fontSize: "4rem", color: "text.disabled", mb: 2 }} />
      <Typography variant="h5" color="textSecondary" gutterBottom>
        No Medicines Found
      </Typography>
      <Typography variant="body1" color="textSecondary">
        There are currently no pet care products available in this category.
      </Typography>
    </Paper>
  );

  // Main Render
  return (
    <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 }, py: 2 }}>
      <MedicineHeader />

      {/* Loading State */}
      {loading && (
        <Box>
          {isMobile ? <MobileSkeletonLoader /> : <TableSkeletonLoader />}
        </Box>
      )}

      {/* Error State */}
      {error && !loading && <ErrorState />}

      {/* Success State */}
      {!loading && !error && (
        <Box>
          {products.length === 0 ? (
            <NoDataState />
          ) : (
            <Box>
              {/* Mobile View */}
              {isMobile ? (
                <Box>
                  <Box sx={{ mb: 3 }}>
                    {products.map((product) => (
                      <MobileProductCard key={product._id} product={product} />
                    ))}
                  </Box>

                  {/* Mobile Pagination */}
                  <Paper sx={{ p: 2, borderRadius: 3, boxShadow: 2 }}>
                    <TablePagination
                      component="div"
                      count={totalCount}
                      page={page}
                      onPageChange={handleChangePage}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      rowsPerPageOptions={[10, 25, 50, 100]}
                      labelRowsPerPage="Items per page:"
                      sx={{
                        "& .MuiTablePagination-toolbar": {
                          paddingLeft: 0,
                          paddingRight: 0,
                        },
                        "& .MuiTablePagination-actions button": {
                          "&:hover": {
                            backgroundColor: medsToWinColors.light,
                          },
                        },
                      }}
                    />
                  </Paper>
                </Box>
              ) : (
                /* Desktop Table View */
                <Paper sx={{ boxShadow: 4, borderRadius: 3 }}>
                  <TableContainer
                    sx={{ borderRadius: "12px 12px 0 0", overflow: "auto" }}
                  >
                    <Table sx={{ minWidth: 800 }}>
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: medsToWinColors.light }}
                        >
                          <TableCell
                            sx={{
                              fontWeight: "bold",
                              color: medsToWinColors.primary,
                              minWidth: 80,
                            }}
                          >
                            Image
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: "bold",
                              color: medsToWinColors.primary,
                              minWidth: 300,
                            }}
                          >
                            Product Details
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: "bold",
                              color: medsToWinColors.primary,
                              minWidth: 80,
                            }}
                          >
                            ID
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: "bold",
                              color: medsToWinColors.primary,
                              minWidth: 200,
                            }}
                          >
                            Pack Size
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: "bold",
                              color: medsToWinColors.primary,
                              minWidth: 100,
                            }}
                          >
                            Price
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: "bold",
                              color: medsToWinColors.primary,
                              minWidth: 80,
                            }}
                          >
                            Type
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: "bold",
                              color: medsToWinColors.primary,
                              minWidth: 120,
                            }}
                          >
                            SKU
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {products.map((product) => (
                          <TableRow
                            key={product._id}
                            sx={{
                              "&:hover": {
                                backgroundColor: "rgba(46, 125, 50, 0.04)",
                                cursor: "pointer",
                              },
                            }}
                            onClick={() =>
                              navigate(`/admin/otc-product-individual-page/${product.sku_id}`)
                            }
                          >
                            <TableCell>
                              {product.image ? (
                                <Avatar
                                  src={product.image}
                                  alt={product.name}
                                  sx={{
                                    width: 60,
                                    height: 60,
                                    borderRadius: 2,
                                  }}
                                  variant="rounded"
                                />
                              ) : (
                                <Avatar
                                  sx={{
                                    width: 60,
                                    height: 60,
                                    backgroundColor: medsToWinColors.light,
                                    color: medsToWinColors.primary,
                                    borderRadius: 2,
                                  }}
                                  variant="rounded"
                                >
                                  <ImageNotSupported />
                                </Avatar>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body1"
                                fontWeight="600"
                                color={medsToWinColors.primary}
                                sx={{ mb: 0.5 }}
                              >
                                {product.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                Category: {product.c_name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={product.id}
                                size="small"
                                sx={{
                                  backgroundColor: medsToWinColors.light,
                                  color: medsToWinColors.primary,
                                  fontWeight: "bold",
                                  fontFamily: "monospace",
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="textSecondary">
                                {product.pack_size_label}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="h6"
                                color={medsToWinColors.primary}
                                fontWeight="bold"
                              >
                                {formatPrice(product.price)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={product.type.toUpperCase()}
                                size="small"
                                sx={{
                                  backgroundColor: "#e3f2fd",
                                  color: "#1976d2",
                                  fontWeight: "bold",
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                color="textSecondary"
                                fontFamily="monospace"
                              >
                                {product.sku_id}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Desktop Pagination */}
                  <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    labelRowsPerPage="Products per page:"
                    labelDisplayedRows={({ from, to, count }) =>
                      `${from}-${to} of ${
                        count !== -1 ? count : `more than ${to}`
                      }`
                    }
                    sx={{
                      borderTop: `1px solid ${medsToWinColors.light}`,
                      "& .MuiTablePagination-toolbar": {
                        paddingLeft: 2,
                        paddingRight: 2,
                      },
                      "& .MuiTablePagination-actions button": {
                        "&:hover": {
                          backgroundColor: medsToWinColors.light,
                        },
                      },
                    }}
                  />
                </Paper>
              )}
            </Box>
          )}
        </Box>
      )}
    </Container>
  );
};

export default PetCareMedicineList;
